import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import InfoIcon from '@mui/icons-material/Info';
import TableViewIcon from '@mui/icons-material/TableChart';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';

import AlertDialog from '../../components/UI/AlertDialog';
import { PrimaryBtn } from '../../components/UI/Buttons';
import BusinessUnitSelector from '../../components/common/BusinessUnitsSelector';
import useKanbanPagination from '../../hooks/useKanbanPagination';
import KanbanBoard from './components/KanbanBoard';
import LeadsTable from './components/LeadsTable';

const Leads = () => {
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(window.location.search);
  const queryBusinessUnitId = queryParams.get('businessUnitId');
  const businessUnitId = queryBusinessUnitId ? parseInt(queryBusinessUnitId || '', 10) : null;

  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');

  const kanbanPagination = useKanbanPagination(businessUnitId);

  const updateBusinessUnitId = (value: number) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('businessUnitId', value.toString());
    navigate(`${window.location.pathname}?${searchParams.toString()}`, { replace: true });
  };

  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newViewMode: 'table' | 'kanban' | null) => {
    if (newViewMode !== null) {
      setViewMode(newViewMode);
    }
  };

  const [followUpSuccessAlert, setFollowUpSuccessAlert] = useState(false);
  const [clientUploadSuccessAlert, setClientUploadSuccessAlert] = useState(false);

  const renderContent = () => {
    if (viewMode === 'table') {
      return <LeadsTable businessUnitId={businessUnitId} />;
    }

    if (!businessUnitId) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" height="400px" color="text.secondary">
          <Typography variant="h6">Selecciona una unidad de negocio para ver el kanban</Typography>
        </Box>
      );
    }

    return (
      <KanbanBoard
        leadsByStatus={kanbanPagination.leadsByStatus}
        statusCounts={kanbanPagination.statusCounts}
        paginationInfo={kanbanPagination.paginationInfo}
        loadingStates={kanbanPagination.loadingStates}
        isInitialLoading={kanbanPagination.isInitialLoading}
        onLoadMore={kanbanPagination.loadMoreLeads}
        getSkeletonCount={kanbanPagination.getSkeletonCount}
      />
    );
  };

  return (
    <div style={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AlertDialog
        openDialog={followUpSuccessAlert}
        setOpenDialog={setFollowUpSuccessAlert}
        header="Follow up creado!"
        msg="Follow up fue creado correctamente"
        cancelMsg="Cerrar"
      />
      <AlertDialog
        openDialog={clientUploadSuccessAlert}
        setOpenDialog={setClientUploadSuccessAlert}
        header="Clientes subidos!"
        msg="Clientes fueron subidos correctamente"
        cancelMsg="Cerrar"
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'stretch', md: 'center' },
          gap: 2,
          pt: 1.5,
          px: 2,
          flexShrink: 0,
        }}
      >
        <Box display="flex" alignItems="center" pl={{ xs: 5, md: 0 }}>
          <Typography variant="h5" textAlign="left">
            Leads
          </Typography>
          <Tooltip title="Aquí se muestran todos los prospectos/leads que han pasado por Zeller. Si un cliente llegó por múltiples fuentes, aparece aquí múltiples veces, pero en la vista de clientes solo aparecerá una vez.">
            <IconButton size="small" sx={{ mr: 2 }}>
              <InfoIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box>
          <BusinessUnitSelector businessUnitId={businessUnitId} setBusinessUnitId={updateBusinessUnitId} small />
        </Box>

        <ToggleButtonGroup value={viewMode} exclusive onChange={handleViewModeChange} size="small">
          <ToggleButton value="table">
            <TableViewIcon sx={{ mr: 1 }} />
            Tabla
          </ToggleButton>
          <ToggleButton value="kanban">
            <ViewKanbanIcon sx={{ mr: 1 }} />
            Kanban
          </ToggleButton>
        </ToggleButtonGroup>

        {viewMode === 'kanban' && (
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Fecha inicial"
              format="DD/MMM/YYYY"
              value={kanbanPagination.startDate}
              onChange={(value) => kanbanPagination.updateDateRange(value, kanbanPagination.endDate)}
              slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
            />
            <DatePicker
              label="Fecha final"
              format="DD/MMM/YYYY"
              value={kanbanPagination.endDate}
              onChange={(value) => kanbanPagination.updateDateRange(kanbanPagination.startDate, value)}
              slotProps={{ textField: { size: 'small', variant: 'outlined' } }}
            />
          </LocalizationProvider>
        )}

        <PrimaryBtn onClick={() => navigate('/campaigns')}>Campañas</PrimaryBtn>
      </Box>

      {viewMode === 'kanban' && kanbanPagination.totalLeads > 0 && (
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary">
            📊 {kanbanPagination.totalLeads} leads total
          </Typography>
        </Box>
      )}

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', p: 1 }}>{renderContent()}</Box>
    </div>
  );
};

export default Leads;
